package com.evoapps.evoshift.domain.comparison

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.daysUntil
import kotlinx.datetime.minus
import kotlinx.datetime.plus

/*
 * SH-228: the Statistics screen's figures, built on the SH-225 comparison engine (its calendar,
 * DayFigures, PeriodFigures, Change and like-for-like rules). A period (a week, a month or a
 * custom run of days) is set against a base:
 *
 *   PREVIOUS_PERIOD  the week before (Monday against Monday), the month before by date (the 31st
 *                    has no counterpart in a 30-day month: "no data", never 0), or the same number
 *                    of days right before a custom range
 *   LAST_YEAR        the same period a year earlier, by weekday (ISO week, SH-225) or, with the
 *                    calendar toggle, by date
 *
 * Every figure counts only days with takings; the change counts only the days with takings on
 * both sides (like for like), so a month to date is set against the same days a year earlier.
 */

/** How the period is picked. */
enum class StatisticsPeriodKind { WEEK, MONTH, CUSTOM }

/** What the period is compared with. */
enum class StatisticsBase { PREVIOUS_PERIOD, LAST_YEAR }

/** The Statistics screen's question: [period] (picked as [kind]) against [base]. */
data class StatisticsQuery(
    val period: DateRange,
    val kind: StatisticsPeriodKind,
    val base: StatisticsBase = StatisticsBase.PREVIOUS_PERIOD,
    /** LAST_YEAR only: compare 24.12 with 24.12 instead of Monday with Monday. */
    val byCalendarDate: Boolean = false,
    /** ISO day the workplace's weeks start on (1 = Monday). */
    val firstDayOfWeek: Int = 1
) {
    init {
        require(period.length <= MAX_DAYS) { "a statistics period holds at most $MAX_DAYS days, not ${period.length}" }
    }

    /** The SH-225 mode the last year is compared in. */
    val lastYearMode: ComparisonMode get() = if (byCalendarDate) ComparisonMode.LAST_YEAR_DATE else ComparisonMode.LAST_YEAR_WEEKDAY

    /** The day [date] (of the period) is compared with; null when there is none (the 31st against a 30-day month). */
    fun counterpart(date: LocalDate): Counterpart? = when (base) {
        StatisticsBase.LAST_YEAR -> ComparisonCalendar.counterpart(date, lastYearMode)
        StatisticsBase.PREVIOUS_PERIOD -> when (kind) {
            StatisticsPeriodKind.WEEK -> Counterpart(date.minus(DatePeriod(days = 7)))
            StatisticsPeriodKind.MONTH -> {
                val before = period.start.minus(DatePeriod(months = 1))
                val first = LocalDate(before.year, before.month, 1)
                val offset = period.start.daysUntil(date)
                val day = first.plus(DatePeriod(days = offset))
                if (day.month == before.month && day.year == before.year) Counterpart(day) else null
            }
            StatisticsPeriodKind.CUSTOM -> Counterpart(date.minus(DatePeriod(days = period.length)))
        }
    }

    /** The days the base reads; null when no day of the period has a counterpart. */
    val baseRange: DateRange? get() = DateRange.covering(period.dates.mapNotNull { counterpart(it)?.date })

    /**
     * What has to be read, as few runs as possible: the whole weeks over the period with the week
     * before the first (week over week), and the base (last year: a run of its own).
     */
    val readRanges: List<DateRange>
        get() {
            val weeks = ComparisonCalendar.readRange(period, ComparisonMode.LAST_WEEK, firstDayOfWeek)
            val base = baseRange ?: return listOf(weeks)
            val touching = base.start <= weeks.end.plus(DatePeriod(days = 1)) && weeks.start <= base.end.plus(DatePeriod(days = 1))
            return if (touching) listOf(weeks.union(base)) else listOf(base, weeks)
        }

    companion object {
        /** The longest custom range (about a quarter), so a screen reads a bounded number of days. */
        const val MAX_DAYS = 92

        /** The week (from [firstDayOfWeek]) holding [date]. */
        fun week(date: LocalDate, firstDayOfWeek: Int): DateRange {
            val start = ComparisonCalendar.weekStart(date, firstDayOfWeek)
            return DateRange(start, start.plus(DatePeriod(days = 6)))
        }

        /** The calendar month holding [date]. */
        fun month(date: LocalDate): DateRange = DateRange.monthOf(date)

        /** [start]..[end], in order and cut to [MAX_DAYS] days (from the start). */
        fun custom(start: LocalDate, end: LocalDate): DateRange {
            val from = minOf(start, end)
            val to = maxOf(start, end)
            val last = from.plus(DatePeriod(days = MAX_DAYS - 1))
            return DateRange(from, minOf(to, last))
        }

        /** The period [steps] periods after [period] (negative: before) of the same [kind]. */
        fun shift(period: DateRange, kind: StatisticsPeriodKind, steps: Int): DateRange = when (kind) {
            StatisticsPeriodKind.WEEK -> DateRange(period.start.plus(DatePeriod(days = 7 * steps)), period.end.plus(DatePeriod(days = 7 * steps)))
            StatisticsPeriodKind.MONTH -> DateRange.monthOf(period.start.plus(DatePeriod(months = steps)))
            StatisticsPeriodKind.CUSTOM -> DateRange(
                period.start.plus(DatePeriod(days = period.length * steps)),
                period.end.plus(DatePeriod(days = period.length * steps))
            )
        }

        /** The read [range] split into runs of at most [maxDays] days (one query each). */
        fun chunks(range: DateRange, maxDays: Int): List<DateRange> {
            val runs = mutableListOf<DateRange>()
            var start = range.start
            while (start <= range.end) {
                val end = minOf(range.end, start.plus(DatePeriod(days = maxDays - 1)))
                runs += DateRange(start, end)
                start = end.plus(DatePeriod(days = 1))
            }
            return runs
        }
    }
}

/** Whether a higher [metric] is the better one (labour cost and labour share: lower is better). */
val ComparisonMetric.higherIsBetter: Boolean
    get() = this != ComparisonMetric.LABOUR_COST && this != ComparisonMetric.LABOUR_PERCENT

/** A sum over days (sales, labour cost, result) rather than a share of two sums (labour %, SPMH). */
val ComparisonMetric.isAdditive: Boolean
    get() = this == ComparisonMetric.NET_SALES || this == ComparisonMetric.LABOUR_COST || this == ComparisonMetric.RESULT

/** One day's figure. */
data class DayValue(val date: LocalDate, val value: Double)

/**
 * The period's figure for one metric: [total] over the days with takings (a sum, or the share of
 * the sums for labour % and SPMH), [averagePerDay] (sums only), the [best] and [worst] days, and
 * the like-for-like [change] over the [comparedDays] that have takings on both sides.
 */
data class StatisticsSummary(
    val metric: ComparisonMetric,
    val total: Double?,
    val averagePerDay: Double?,
    /** Days of the period with takings. */
    val tradedDays: Int,
    val periodDays: Int,
    val best: DayValue?,
    val worst: DayValue?,
    val change: Change,
    val comparedDays: Int,
    /** Days with takings whose counterpart has none (or that have no counterpart at all). */
    val missingBase: Int
) {
    /** Nothing to compare: no day has takings on both sides ("Brak danych z zeszłego roku"). */
    val baseMissing: Boolean get() = comparedDays == 0
}

/** A weekday's average over the period ([current], [currentDays] days) and over the base. */
data class WeekdayAverage(
    val dayOfWeek: DayOfWeek,
    val current: Double?,
    val currentDays: Int,
    val base: Double?,
    val baseDays: Int
) {
    val change: Change get() = Change(current, base)
}

/**
 * SH-228: [query] worked out from what [comparisons] knows (the period, its weeks and its base,
 * as the daily results count them).
 */
class SalesStatistics(val query: StatisticsQuery, private val comparisons: SalesComparisons) {

    /** Each day of the period against its counterpart (a day without one: no base). */
    val days: List<DayComparison> = query.period.dates.map { date ->
        val counterpart = query.counterpart(date)
        if (counterpart != null) comparisons.dayAgainst(date, counterpart)
        else comparisons.dayAgainst(date, Counterpart(date), withBase = false)
    }

    /** Each week over the period against the week before, like for like. */
    val weeks: List<WeekComparison> = comparisons.weeks(query.period, query.firstDayOfWeek)

    /** Days with takings on both sides. */
    val compared: List<DayComparison> get() = days.filter { it.compared }

    /** A day's figure (null: no takings, no data). */
    fun value(day: DayComparison, metric: ComparisonMetric): Double? = day.current?.value(metric)

    fun summary(metric: ComparisonMetric): StatisticsSummary {
        val traded = days.mapNotNull { it.current }.filter { it.hasTakings }
        val values = traded.mapNotNull { day -> day.value(metric)?.let { DayValue(day.date, it) } }
        val period = PeriodFigures.of(traded)
        val total = period?.value(metric)
        val average = if (metric.isAdditive && period != null && period.days > 0) total?.div(period.days) else null
        val best = if (metric.higherIsBetter) values.maxByOrNull { it.value } else values.minByOrNull { it.value }
        val worst = if (metric.higherIsBetter) values.minByOrNull { it.value } else values.maxByOrNull { it.value }
        val both = compared
        val change = Change(
            PeriodFigures.of(both.mapNotNull { it.current })?.value(metric),
            PeriodFigures.of(both.mapNotNull { it.base })?.value(metric)
        )
        return StatisticsSummary(
            metric = metric,
            total = total,
            averagePerDay = average,
            tradedDays = traded.size,
            periodDays = days.size,
            best = best,
            worst = worst,
            change = change,
            comparedDays = both.size,
            missingBase = days.count { it.baseMissing }
        )
    }

    /** Each weekday (from the workplace's first) averaged over the period and over the base. */
    fun weekdays(metric: ComparisonMetric): List<WeekdayAverage> {
        val first = query.firstDayOfWeek.coerceIn(1, 7)
        return (0 until 7).map { i ->
            val dow = DayOfWeek((first - 1 + i) % 7 + 1)
            val mine = days.filter { it.date.dayOfWeek == dow }
            val current = mine.mapNotNull { it.current }.filter { it.hasTakings }
            val base = mine.mapNotNull { it.base }.filter { it.hasTakings }
            WeekdayAverage(dow, average(current, metric), current.size, average(base, metric), base.size)
        }
    }

    private fun average(days: List<DayFigures>, metric: ComparisonMetric): Double? {
        val figures = PeriodFigures.of(days) ?: return null
        val value = figures.value(metric) ?: return null
        return if (metric.isAdditive) value / figures.days else value
    }
}
